export const STEP = 30;
export const LENGTH = 60;
export const FIRST_FREE_FROM = 9 * 60;

function overlaps(busy, start, end) {
  return busy.some(([from, to]) => start < to && end > from);
}

function slotEnd(busy, start, last) {
  const next = busy.map(([from]) => from).filter((from) => from > start);
  return Math.min(start + LENGTH, last, ...next);
}

export function snapSlot(minute, busy, [first, last]) {
  const snapped = Math.floor(minute / STEP) * STEP;
  const start = Math.max(first, Math.min(last - STEP, snapped));
  if (overlaps(busy, start, start + STEP)) return firstFreeSlot(busy, [first, last], start);
  return { start, end: slotEnd(busy, start, last) };
}

export function firstFreeSlot(busy, [first, last], from = FIRST_FREE_FROM) {
  let start = Math.max(first, Math.min(from, last - STEP));
  while (start < last - STEP && overlaps(busy, start, start + STEP)) start += STEP;
  if (overlaps(busy, start, start + STEP)) start = first;
  return { start, end: slotEnd(busy, start, last) };
}

export function slotAfter(busy, bounds, from) {
  const slot = firstFreeSlot(busy, bounds, from);
  return from !== undefined && slot.start < from ? firstFreeSlot(busy, bounds) : slot;
}

function parts(minutes) {
  const hour = Math.floor(minutes / 60) % 24;
  return { hour, minute: minutes % 60, twelve: ((hour + 11) % 12) + 1, pm: hour >= 12 };
}

export function clockLabel(minutes) {
  const { twelve, minute, pm } = parts(minutes);
  return `${twelve}:${String(minute).padStart(2, "0")} ${pm ? "PM" : "AM"}`;
}

export function compactLabel(minutes) {
  const { twelve, minute, pm } = parts(minutes);
  return `${twelve}${minute ? `:${String(minute).padStart(2, "0")}` : ""}${pm ? "p" : "a"}`;
}

export function lengthLabel(minutes) {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return [hours && `${hours}h`, rest && `${rest}m`].filter(Boolean).join(" ");
}

export function dateTimeParam(isoDate, minutes) {
  const clamped = Math.min(minutes, 24 * 60 - 1);
  const hour = String(Math.floor(clamped / 60)).padStart(2, "0");
  const minute = String(clamped % 60).padStart(2, "0");
  return `${isoDate} ${hour}:${minute}`;
}

function kicker(group, role) {
  const parts = role && role.toLowerCase() !== group.toLowerCase() ? [group, role] : [group];
  return parts.join(" · ").toUpperCase();
}

export function slotCopy({ kind, role, name, dayLabel, start, end }) {
  const room = kind === "room";
  return {
    kicker: kicker(room ? "Room" : "Staff", role),
    when: `${dayLabel} · ${clockLabel(start)}`,
    length: lengthLabel(end - start),
    who: name,
    bookSub: `${clockLabel(start)} ${room ? "in" : "with"} ${name}`,
    blockSub: `${clockLabel(start)} – ${clockLabel(end)} · mark unavailable`,
    ghost: `${compactLabel(start)}–${compactLabel(end)}`,
  };
}

export const RESIZE_STEP = 15;
export const RESIZE_MIN = 30;

export function resizeSlot({ start, end, edge, minutes, busy = [], bounds: [first, last], step = RESIZE_STEP, min: least = RESIZE_MIN }) {
  const min = Math.min(least, end - start);
  if (edge === "start") {
    const floor = Math.max(first, ...busy.map(([, to]) => to).filter((to) => to <= start));
    const target = Math.round((start + minutes) / step) * step;
    return { start: Math.min(Math.max(target, floor), end - min), end };
  }
  const ceiling = Math.min(last, ...busy.map(([from]) => from).filter((from) => from >= end));
  const target = Math.round((end + minutes) / step) * step;
  return { start, end: Math.max(Math.min(target, ceiling), start + min) };
}

export function moveSlot({ start, end, minutes, busy = [], bounds: [first, last], step = RESIZE_STEP }) {
  const length = end - start;
  const floor = Math.max(first, ...busy.map(([, to]) => to).filter((to) => to <= start));
  const ceiling = Math.min(last, ...busy.map(([from]) => from).filter((from) => from >= end));
  const target = Math.round((start + minutes) / step) * step;
  const moved = Math.min(Math.max(target, floor), Math.max(floor, ceiling - length));
  return { start: moved, end: moved + length };
}

export function fitToService(slot, minutes, resized) {
  if (resized || !(minutes > 0)) return slot;
  return { start: slot.start, end: slot.start + minutes };
}

export function canBlock({ kind, you, employee }) {
  if (kind === "unassigned") return false;
  if (!employee) return true;
  return kind === "staff" && you;
}

export function returnPath({ pathname, search }) {
  return `${pathname}${search}`;
}

export function bookingHref(base, { date, slot, returnTo, kind, laneId, location, serviceId, seriesId }) {
  const params = new URLSearchParams({ start_at_date: date, start_at_time: clockLabel(slot.start), duration: String(slot.end - slot.start), return_to: returnTo });
  if (kind === "staff" && laneId) params.append("user_id", laneId);
  if (kind === "room" && laneId) params.append("location_resource_id", laneId);
  if (location) params.append("location_id", location);
  if (serviceId) params.append("service_id", serviceId);
  if (seriesId) params.append("series_id", seriesId);
  return `${base}?${params.toString()}`;
}

export function blockHref(base, fields, returnTo) {
  const params = new URLSearchParams();
  Object.entries(fields).forEach(([name, value]) => {
    if (value === "" || value === undefined || value === null) return;
    params.append(name === "location_resource_id" ? "availability_exception[location_resource_ids][]" : `availability_exception[${name}]`, value);
  });
  params.append("return_to", returnTo);
  return `${base}?${params.toString()}`;
}

export function blockFields({ kind, laneId, location, fallbackLocation, date, slot }) {
  return {
    start_at: dateTimeParam(date, slot.start),
    end_at: dateTimeParam(date, slot.end),
    block_provider: kind === "room" ? "false" : "true",
    all_rooms: "0",
    user_id: kind === "staff" ? laneId : "",
    location_resource_id: kind === "room" ? laneId : "",
    location_id: location || fallbackLocation,
  };
}
